import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { platformMediaAcquisitionManifestSchema, platformMediaAcquisitionRequestSchema, } from '@crest/platform-contracts/media-acquisition';
import { createContext, useCallback, useContext, useEffect, useRef, } from 'react';
import { usePlatformBootstrap } from '../runtime/platform-bootstrap-provider.js';
export class PlatformMediaAcquisitionBusyError extends Error {
    constructor() {
        super('Another media acquisition is already active.');
        this.name = 'PlatformMediaAcquisitionBusyError';
    }
}
export class PlatformMediaAcquisitionCancelledError extends Error {
    constructor() {
        super('Media acquisition was cancelled.');
        this.name = 'PlatformMediaAcquisitionCancelledError';
    }
}
export class PlatformMediaAcquisitionRejectedError extends Error {
    constructor(message) {
        super(message);
        this.name = 'PlatformMediaAcquisitionRejectedError';
    }
}
export class PlatformMediaAcquisitionLeaseCoordinator {
    cancelActive = null;
    get active() {
        return this.cancelActive !== null;
    }
    begin(cancel) {
        if (this.cancelActive !== null)
            throw new PlatformMediaAcquisitionBusyError();
        this.cancelActive = cancel;
        let released = false;
        return () => {
            if (released)
                return;
            released = true;
            if (this.cancelActive === cancel)
                this.cancelActive = null;
        };
    }
    cancel(reason = new PlatformMediaAcquisitionCancelledError()) {
        const active = this.cancelActive;
        if (active === null)
            return;
        this.cancelActive = null;
        active(reason);
    }
}
const processLease = new PlatformMediaAcquisitionLeaseCoordinator();
const PlatformMediaAcquisitionContext = createContext(null);
export function PlatformMediaAcquisitionProvider(props) {
    const bootstrap = usePlatformBootstrap();
    const input = useRef(null);
    const pending = useRef(null);
    const identityKey = [
        bootstrap.tenant.id,
        bootstrap.actor.userId,
        bootstrap.membership.id,
        bootstrap.revisions.session,
    ].join(':');
    const rejectPending = useCallback((reason) => {
        const current = pending.current;
        if (current === null)
            return;
        pending.current = null;
        current.release();
        current.reject(reason);
        if (input.current !== null)
            input.current.value = '';
    }, []);
    useEffect(() => {
        return () => processLease.cancel(new PlatformMediaAcquisitionCancelledError());
    }, [identityKey]);
    useEffect(() => {
        const cancelEmptySelection = () => {
            const observed = pending.current;
            if (observed === null)
                return;
            window.setTimeout(() => {
                if (pending.current === observed && input.current?.files?.length === 0) {
                    processLease.cancel(new PlatformMediaAcquisitionCancelledError());
                }
            }, 0);
        };
        window.addEventListener('focus', cancelEmptySelection);
        return () => window.removeEventListener('focus', cancelEmptySelection);
    }, []);
    const acquire = useCallback((rawRequest) => {
        const request = platformMediaAcquisitionRequestSchema.parse(rawRequest);
        const element = input.current;
        if (element === null) {
            return Promise.reject(new PlatformMediaAcquisitionRejectedError('The media acquisition input is unavailable.'));
        }
        return new Promise((resolve, reject) => {
            const release = processLease.begin((reason) => rejectPending(reason));
            pending.current = { request, resolve, reject, release };
            element.value = '';
            element.accept = request.acceptedContentTypes.join(',');
            element.setAttribute('capture', request.facing ?? 'environment');
            try {
                element.click();
            }
            catch (caught) {
                rejectPending(caught instanceof Error ? caught : new PlatformMediaAcquisitionRejectedError('The media acquisition input could not be opened.'));
            }
        });
    }, [rejectPending]);
    const selectFile = (event) => {
        const current = pending.current;
        const file = event.currentTarget.files?.[0];
        if (current === null || file === undefined)
            return;
        if (!current.request.acceptedContentTypes.includes(file.type)) {
            rejectPending(new PlatformMediaAcquisitionRejectedError('The selected media type is not allowed.'));
            return;
        }
        if (file.size > current.request.maximumFileSizeBytes) {
            rejectPending(new PlatformMediaAcquisitionRejectedError('The selected media is larger than the configured limit.'));
            return;
        }
        const manifest = platformMediaAcquisitionManifestSchema.parse({
            schemaVersion: 1,
            acquisitionId: crypto.randomUUID(),
            idempotencyKey: current.request.idempotencyKey,
            tenantId: bootstrap.tenant.id,
            actorUserId: bootstrap.actor.userId,
            membershipId: bootstrap.membership.id,
            sessionRevision: bootstrap.revisions.session,
            moduleId: current.request.moduleId,
            resourceType: current.request.resourceType,
            resourceId: current.request.resourceId,
            purpose: current.request.purpose,
            mode: current.request.mode,
            adapter: 'browser-os-capture-input',
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
            selectedAt: new Date().toISOString(),
        });
        pending.current = null;
        current.release();
        current.resolve({ file, manifest });
        event.currentTarget.value = '';
    };
    return (_jsxs(PlatformMediaAcquisitionContext, { value: { acquire }, children: [props.children, _jsx("input", { "aria-hidden": "true", "data-platform-media-acquisition": "browser-os-capture-input", onChange: selectFile, ref: input, style: { display: 'none' }, tabIndex: -1, type: "file" })] }));
}
export function usePlatformMediaAcquisition() {
    const value = useContext(PlatformMediaAcquisitionContext);
    if (value === null)
        throw new Error('PlatformMediaAcquisitionProvider is missing.');
    return value;
}
//# sourceMappingURL=platform-media-acquisition-provider.js.map